import React, { useMemo, useState } from 'react';
import {
  Button,
  InputNumber,
  message,
  Select,
  Space,
  Table,
  Tooltip,
  Typography,
} from 'antd';
import { fullWidthStyle } from 'utils';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { thousandFormatter } from 'utils/forms/formatter';
import { ActionsWrapper } from 'ui/ActionsWrapper';
import { REQUEST_LOADING } from 'constants/alerts';
import { PaymentsService } from 'services/PaymentsService';

function GenerateServicePaymentForm({
  packagesLoading,
  packages,
  clientId,
  onSuccess,
}) {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(false);

  const canGenerate = useMemo(() => {
    if (services.length === 0) {
      return false;
    }

    return services.every((service) => {
      return service.package_id && service.option_id && service.custom_amount;
    });
  }, [services]);

  const columns = [
    {
      width: 200,
      title: 'Тариф / Услуга',
      key: 'package_id',
      dataIndex: 'package_id',
      render: (packageId, record, idx) => (
        <Select
          loading={packagesLoading}
          style={fullWidthStyle}
          placeholder="Выбрать тариф / услугу"
          showSearch
          optionFilterProp="children"
          value={packageId}
          onChange={(serviceId) => handleServiceChange(serviceId, idx)}
        >
          {packages
            .filter((item) => item.is_public)
            .map(({ id, title }) => (
              <Select.Option key={id} value={id}>
                {title}
              </Select.Option>
            ))}
        </Select>
      ),
    },
    {
      width: 200,
      title: 'Опция',
      key: 'option_id',
      dataIndex: 'option_id',
      render: (optionId, record, idx) => (
        <Select
          style={fullWidthStyle}
          placeholder="Выбрать опцию"
          value={optionId}
          disabled={!record?.package_id}
          onChange={(optionId) =>
            handleOptionChange(optionId, record?.package_id, idx)
          }
        >
          {record?.options.map(({ id, title }) => (
            <Select.Option key={id} value={id}>
              {title}
            </Select.Option>
          ))}
        </Select>
      ),
    },
    {
      width: 160,
      title: 'Стоимость',
      key: 'custom_amount',
      dataIndex: 'custom_amount',
      render: (price, record, idx) => (
        <InputNumber
          style={fullWidthStyle}
          placeholder="Цена"
          disabled
          value={price}
          onChange={(val) => {
            handleFieldChange(val, 'custom_amount', idx);
            handleDiscountChange(record?.discount, idx);
          }}
          controls={false}
          formatter={thousandFormatter}
        />
      ),
    },
    {
      width: 160,
      title: 'Скидка в тенге',
      key: 'discount_price',
      dataIndex: 'discount_price',
      render: (price, record, idx) => (
        <InputNumber
          style={fullWidthStyle}
          placeholder="Введите скидку в тенге"
          value={price}
          disabled={!record?.option_id}
          max={record.custom_amount}
          onChange={(val) => {
            handleFieldChange(val, 'discount_price', idx);
            handleRealDiscountChange(record?.discount_price, idx);
          }}
          controls={false}
          formatter={thousandFormatter}
        />
      ),
    },
    {
      width: 160,
      title: 'Скидка в процентах',
      key: 'discount_percent',
      dataIndex: 'discount_percent',
      render: (discount_percent, record, idx) => (
        <InputNumber
          style={fullWidthStyle}
          placeholder="Введите скидку в процентах"
          value={discount_percent}
          disabled={!record?.option_id}
          showSearch
          controls={false}
          max={100}
          optionFilterProp="children"
          allowClear
          onChange={(val) => handleDiscountChange(val, idx)}
          onClear={() => handleDiscountChange(null, idx)}
        >
          {/* {serviceDiscountOptions.map(({ text, val }) => (
            <Select.Option key={val} value={val}>
              {text}
            </Select.Option>
          ))} */}
        </InputNumber>
      ),
    },
    {
      width: 160,
      align: 'center',
      title: 'Сумма',
      key: 'total',
      dataIndex: 'total',
      render: (totalPrice) =>
        totalPrice ? thousandFormatter(totalPrice) : '---',
    },
    {
      width: 130,
      align: 'center',
      title: 'Действия',
      key: 'operation',
      render: (_, record, idx) => {
        return (
          <Tooltip title="Удалить">
            <Button
              type="default"
              danger
              size="small"
              icon={<DeleteOutlined />}
              onClick={() => removeService(idx)}
            />
          </Tooltip>
        );
      },
    },
  ];

  const addService = () => {
    const newService = {
      package_id: null,
      option_id: null,
      custom_amount: 0,
      options: [],
    };
    setServices([...services, newService]);
  };

  const removeService = (index) => {
    const servicesCopy = [...services];
    servicesCopy.splice(index, 1);
    setServices(servicesCopy);
  };

  const handleDiscountChange = (discountValue, idx) => {
    const servicesCopy = [...services];
    const updatePrice = Math.round(
      (discountValue / 100) * servicesCopy[idx].custom_amount
    );

    if (discountValue) {
      servicesCopy[idx].discount_percent = discountValue;
      servicesCopy[idx].discount_price = updatePrice.toFixed(2);
      servicesCopy[idx].total = Math.round(
        servicesCopy[idx].custom_amount -
          (servicesCopy[idx].custom_amount * discountValue) / 100
      );
    } else {
      servicesCopy[idx].total = servicesCopy[idx].custom_amount;
      servicesCopy[idx].discount_percent = null;
      servicesCopy[idx].discount_price = null;
    }

    setServices(servicesCopy);
  };

  const handleRealDiscountChange = (value, idx) => {
    const servicesCopy = [...services];
    const updatedDiscount = (value / servicesCopy[idx].custom_amount) * 100;

    if (value) {
      servicesCopy[idx].discount_price = value;
      servicesCopy[idx].discount_percent = updatedDiscount.toFixed(2);
      servicesCopy[idx].total = Math.round(
        servicesCopy[idx].custom_amount - value
      );
    } else {
      servicesCopy[idx].total = servicesCopy[idx].custom_amount;
      servicesCopy[idx].discount_price = null;
      servicesCopy[idx].discount_percent = null;
    }

    setServices(servicesCopy);
  };

  const handleServiceChange = (serviceId, idx) => {
    const { id, options } = packages.find((item) => item.id === serviceId);

    const servicesCopy = [...services];
    const currService = servicesCopy[idx];

    currService.package_id = id;
    currService.options = options;

    // Reset selected all columns when package change
    currService.option_id = null;
    currService.custom_amount = null;
    currService.discount_percent = null;
    currService.total = null;
    currService.discount_price = null;

    setServices(servicesCopy);
  };

  const handleOptionChange = (optionId, packageId, idx) => {
    const servicesCopy = [...services];

    const { options } = packages.find((p) => p.id === packageId);
    const { id, price, discount_percent, price_discount } = options.find(
      (o) => o.id === optionId
    );

    servicesCopy[idx].option_id = id;
    servicesCopy[idx].custom_amount = price;
    servicesCopy[idx].discount = discount_percent;
    servicesCopy[idx].total = price_discount ?? price;

    setServices(servicesCopy);
  };

  const handleFieldChange = (value, fieldKey, index) => {
    const servicesCopy = [...services];
    servicesCopy[index][fieldKey] = value;
    setServices(servicesCopy);
  };

  const handleSubmit = async () => {
    const normalizedServices = services.map((s) => {
      const { package_id, option_id, discount_percent, discount_price, total } =
        s;
      return {
        package_id,
        option_id,
        discount_percent: +discount_percent,
        discount_price: +discount_price,
        payment_price: +total,
      };
    });

    const payload = {
      client_id: clientId,
      type: 'kaspi',
      packages: normalizedServices,
    };

    const hide = message.loading(REQUEST_LOADING);
    setLoading(true);

    try {
      const isGenerated = await PaymentsService.generateKaspiPayment(payload);

      if (isGenerated) {
        setServices([]);
        setLoading(false);
        message.success('Платеж успешно выставлен');
        onSuccess();
      }
    } catch (err) {
      setLoading(false);
    } finally {
      hide();
    }
  };

  const renderSummary = () => {
    return (
      <Table.Summary.Row>
        <Table.Summary.Cell>
          <Button type="secondary" icon={<PlusOutlined />} onClick={addService}>
            Еще
          </Button>
        </Table.Summary.Cell>
        <Table.Summary.Cell>&nbsp;</Table.Summary.Cell>
        <Table.Summary.Cell>&nbsp;</Table.Summary.Cell>
        <Table.Summary.Cell>&nbsp;</Table.Summary.Cell>
      </Table.Summary.Row>
    );
  };

  return (
    <div>
      <Table
        style={fullWidthStyle}
        columns={columns}
        size="small"
        dataSource={services}
        pagination={false}
        summary={renderSummary}
        scroll={{ x: 1200 }}
      />

      <ActionsWrapper placeEnd style={{ marginTop: '20px' }}>
        <Button
          disabled={!canGenerate || loading}
          type="primary"
          onClick={handleSubmit}
        >
          Сформировать
        </Button>
      </ActionsWrapper>

      <Space align="start" direction="vertical">
        <Typography.Text type="secondary">
          * Внимание! После успешной оплаты оформится фискальный чек.
        </Typography.Text>

        <Typography.Text type="secondary">
          * После создания платежа клиенту придет Push уведомление для перехода
          на Kaspi. Так же у клиента в разделе Сообщения / Уведомления появится
          временная ссылка для перехода на Kaspi.
        </Typography.Text>
      </Space>
    </div>
  );
}

export { GenerateServicePaymentForm };
